/**
 * Ingest Devices Verwaltung - Raspberry Pi Enrollment
 *
 * Step 1: Zeigt alle Geräte (pending, approved, rejected)
 * - Pending: Approve/Reject Buttons
 * - Approved: Status-Anzeige + Remote Commands
 * - Rejected: Status-Anzeige
 *
 * Step 4: Remote Commands
 * - Command-Buttons für freigegebene Geräte
 * - Command-History Anzeige
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Cpu,
  RefreshCw,
  Search,
  Check,
  X,
  Clock,
  CircleSlash,
  CircleCheck,
  Terminal,
  RotateCcw,
  ArrowUpCircle,
  FileText,
  Loader2,
  ChevronRight,
  Wifi,
  WifiOff,
  Network,
  HardDrive,
  Thermometer,
  Activity,
  Globe,
  Router,
  Server,
  Settings,
  Save,
  Plus,
  Trash2,
  FolderOpen,
  XCircle,
  Info,
  Zap,
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  Copy,
  Check as CheckIcon,
  Pencil,
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { DeviceHistoryCharts } from '../../components/DeviceHistoryCharts';

type DeviceStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'BANNED' | 'DECOMMISSIONED';

interface InterfaceStats {
  rxBytes: number;
  txBytes: number;
  rxPackets: number;
  txPackets: number;
  rxErrors: number;
  txErrors: number;
  rxDropped: number;
  txDropped: number;
}

type InterfaceRole = 'uplink' | 'isolated';

interface InterfaceInfo {
  name: string;
  type: 'ethernet' | 'wlan';
  linkUp: boolean;
  mac?: string;
  ipv4Addrs?: string[];
  ipv6Addrs?: string[];
  gateway?: string;
  dns?: string[];
  metric?: number;
  role?: InterfaceRole;
  isActiveUplink?: boolean;
  dhcpOrStatic?: string;
  ssid?: string;
  signalDbm?: number;
  txBitrate?: string;
  frequency?: string;
  channel?: number;
  stats?: InterfaceStats;
}

interface InterfaceConfigEntry {
  role: InterfaceRole;
  purpose?: string;
}

interface DeviceInterfaceConfig {
  [interfaceName: string]: InterfaceConfigEntry;
}

interface HealthPayload {
  timestamp: string;
  agentVersion?: string;
  system?: {
    uptimeSeconds?: number;
    cpuTempCelsius?: number;
    cpuLoad1m?: number;
    cpuLoad5m?: number;
    cpuLoad15m?: number;
    cpuCores?: number;
    cpuModel?: string;
    memTotalMb?: number;
    memUsedMb?: number;
    memPercent?: number;
    diskTotalMb?: number;
    diskUsedMb?: number;
    diskPercent?: number;
    osRelease?: string;
    kernel?: string;
  };
  network?: {
    interfaces: InterfaceInfo[];
    defaultRouteInterface?: string;
    defaultGateway?: string;
    dnsServers?: string[];
  };
  vpn?: {
    netbirdConnected: boolean;
    netbirdIp?: string;
    mgmtUrl?: string;
  };
  storage?: {
    wearPercent?: number | null;
    spareAvailPercent?: number | null;
    powerOnHours?: number | null;
    unsafeShutdowns?: number | null;
    mediaErrors?: number | null;
    writtenGb?: number | null;
    temperatureC?: number | null;
    criticalWarning?: number | null;
  };
  services?: Record<string, string>;
  target?: {
    internetReachable: boolean;
    gatewayReachable: boolean;
  };
  connectivity?: {
    internetLatencyMs?: number;
    gatewayLatencyMs?: number;
    dnsLatencyMs?: number;
    packetLossPercent?: number;
    throttlingDetected: boolean;
  };
  role?: {
    name: string;
    configVersion: number;
    runnerState: 'idle' | 'starting' | 'running' | 'error' | 'stopped';
    feedsActive: number;
    lastError?: string | null;
    lastConfigAppliedAt?: string;
  };
  recentEvents?: DeviceEvent[];
}

interface DeviceEvent {
  ts: string;
  level: 'info' | 'warn' | 'error';
  event: string;
  msg: string;
  details?: Record<string, unknown>;
}

type DeviceRole = 'GENERIC' | 'TWA' | 'WATER_METER' | 'CONSUMABLES';

interface TwaMachine {
  machineCode: 'TWA_P25' | 'TWA_P50';
  displayName: string;
  basePath: string;
  feeds: string[];
}

interface TwaRoleConfig {
  smbHost: string;
  smbShare: string;
  smbUsername?: string;
  smbPassword?: string;
  machines: TwaMachine[];
}

interface IngestDevice {
  id: string;
  deviceId: string;
  hardwareId: string | null;
  piModel: string | null;
  piSerial: string | null;
  status: DeviceStatus;
  requestedAt: string;
  lastSeenAt?: string | null;
  role: DeviceRole;
  configJson?: TwaRoleConfig | null;
  configVersion: number;
  configUpdatedAt?: string | null;
  lastConfigAppliedVersion?: number | null;
  lastCpuTemp?: number | null;
  lastMemPercent?: number | null;
  lastDiskPercent?: number | null;
  lastKernel?: string | null;
  lastOsRelease?: string | null;
  lastWlanSsid?: string | null;
  lastWlanSignal?: number | null;
  lastNetbirdIp?: string | null;
  lastGateway?: string | null;
  lastHealthPayload?: HealthPayload | null;
  // Serverseitig abgeleitet (nicht mehr im Browser berechnen)
  connectivityStatus?: 'online' | 'stale' | 'offline' | 'never_seen';
  effectiveRunnerState?: string;
  secondsSinceLastSeen?: number | null;
  interfaceConfig?: Record<string, {
    role?: InterfaceRole;
    method?: 'dhcp' | 'static';
    ipAddress?: string;
    gateway?: string;
    dns?: string[];
    metric?: number | null;
    purpose?: string;
  }> | null;
}

type EffectiveRunnerState = NonNullable<HealthPayload['role']>['runnerState'] | 'offline' | 'unknown';

interface DevicesResponse {
  devices: IngestDevice[];
  total: number;
  pendingCount: number;
}

interface IngestCommand {
  id: string;
  command: string;
  status: string;
  output?: string | null;
  createdAt: string;
  executedAt?: string | null;
}

interface AgentArtifact {
  id: string;
  arch: string;
  channel: string;
  version: string;
  sha256: string;
  size: number;
  isActive: boolean;
  uploadedAt: string;
  uploadedBy: string;
}

// ============================================
// Interface ViewModel Types + Builder
// ============================================

type FieldStatus = 'applied' | 'queued' | 'not_managed';

interface FieldDiff {
  field: string;
  status: FieldStatus;
  appliedValue: string | number | null | undefined;
  desiredValue: string | number | null | undefined;
}

interface InterfaceViewModel {
  name: string;
  type: 'ethernet' | 'wlan';
  linkUp: boolean;
  mac?: string;
  isActiveUplink: boolean;
  // Applied State (from health payload)
  applied: {
    role: InterfaceRole;
    method: string;
    ipAddress?: string;
    gateway?: string;
    dns?: string[];
    metric?: number;
  };
  // Desired State (from saved config)
  desired: {
    role?: InterfaceRole;
    method?: 'dhcp' | 'static';
    ipAddress?: string;
    gateway?: string;
    dns?: string[];
    metric?: number | null;
    purpose?: string;
  } | null;
  // Per-field diff
  diffs: FieldDiff[];
  // Summary status
  status: 'applied' | 'queued';
  // WLAN specific
  wlan?: {
    ssid?: string;
    signalDbm?: number;
    channel?: number;
  };
}

type SavedInterfaceConfig = {
  role?: InterfaceRole;
  method?: 'dhcp' | 'static';
  ipAddress?: string;
  gateway?: string;
  dns?: string[];
  metric?: number | null;
  purpose?: string;
};

function buildInterfaceViewModel(
  iface: InterfaceInfo,
  savedConfig: SavedInterfaceConfig | null | undefined,
  defaultGateway?: string,
  defaultDns?: string[]
): InterfaceViewModel {
  const diffs: FieldDiff[] = [];
  
  // Applied state from health payload
  const applied = {
    role: (iface.role || 'uplink') as InterfaceRole,
    method: iface.dhcpOrStatic || 'dhcp',
    ipAddress: iface.ipv4Addrs?.[0],
    gateway: iface.gateway || (iface.isActiveUplink ? defaultGateway : undefined),
    dns: iface.dns?.length ? iface.dns : (iface.isActiveUplink ? defaultDns : undefined),
    metric: iface.metric,
  };
  
  // Desired state from saved config (can be null = not managed)
  const desired = savedConfig ? {
    role: savedConfig.role,
    method: savedConfig.method,
    ipAddress: savedConfig.ipAddress,
    gateway: savedConfig.gateway,
    dns: savedConfig.dns,
    metric: savedConfig.metric,
    purpose: savedConfig.purpose,
  } : null;
  
  // Compute diffs only if we have desired config
  if (desired) {
    // Role diff
    if (desired.role !== undefined) {
      const appliedRole = applied.role;
      const desiredRole = desired.role;
      diffs.push({
        field: 'role',
        status: appliedRole === desiredRole ? 'applied' : 'queued',
        appliedValue: appliedRole,
        desiredValue: desiredRole,
      });
    }
    
    // Method diff
    if (desired.method !== undefined) {
      const appliedMethod = applied.method;
      const desiredMethod = desired.method;
      diffs.push({
        field: 'method',
        status: appliedMethod === desiredMethod ? 'applied' : 'queued',
        appliedValue: appliedMethod,
        desiredValue: desiredMethod,
      });
    }
    
    // IP diff (only for static)
    if (desired.method === 'static' && desired.ipAddress !== undefined) {
      const appliedIp = applied.ipAddress;
      const desiredIp = desired.ipAddress;
      diffs.push({
        field: 'ipAddress',
        status: appliedIp === desiredIp ? 'applied' : 'queued',
        appliedValue: appliedIp,
        desiredValue: desiredIp,
      });
    }
    
    // Gateway diff (only for static)
    if (desired.method === 'static' && desired.gateway !== undefined) {
      const appliedGw = applied.gateway;
      const desiredGw = desired.gateway;
      diffs.push({
        field: 'gateway',
        status: appliedGw === desiredGw ? 'applied' : 'queued',
        appliedValue: appliedGw,
        desiredValue: desiredGw,
      });
    }
    
    // DNS diff (compare as sorted sets)
    if (desired.dns !== undefined) {
      const appliedDns = (applied.dns || []).slice().sort().join(',');
      const desiredDns = (desired.dns || []).slice().sort().join(',');
      diffs.push({
        field: 'dns',
        status: appliedDns === desiredDns ? 'applied' : 'queued',
        appliedValue: applied.dns?.join(', ') || '-',
        desiredValue: desired.dns?.join(', ') || '-',
      });
    }
    
    // Metric diff: null = auto, undefined = not managed, number = explicit
    if (desired.metric !== undefined) {
      const appliedMetric = applied.metric;
      const desiredMetric = desired.metric;
      // Auto (null) vs actual value
      const appliedStr = appliedMetric !== undefined ? String(appliedMetric) : 'auto';
      const desiredStr = desiredMetric !== null && desiredMetric !== undefined ? String(desiredMetric) : 'auto';
      diffs.push({
        field: 'metric',
        status: appliedStr === desiredStr ? 'applied' : 'queued',
        appliedValue: appliedStr,
        desiredValue: desiredStr,
      });
    }
  }
  
  // Summary: any queued => interface is queued
  const hasQueued = diffs.some(d => d.status === 'queued');
  
  return {
    name: iface.name,
    type: iface.type,
    linkUp: iface.linkUp,
    mac: iface.mac,
    isActiveUplink: iface.isActiveUplink || false,
    applied,
    desired,
    diffs,
    status: hasQueued ? 'queued' : 'applied',
    wlan: iface.type === 'wlan' ? {
      ssid: iface.ssid,
      signalDbm: iface.signalDbm,
      channel: iface.channel,
    } : undefined,
  };
}

// OutputPanel: Einheitliche Darstellung fuer Command-Outputs und Event-Details
function OutputPanel({ 
  content, 
  label,
  maxHeight = '20rem',
}: { 
  content: string; 
  label?: string;
  maxHeight?: string;
}) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 border-b border-slate-200">
        <span className="text-xs text-slate-500">{label || 'Output'} ({content.length} Zeichen)</span>
        <button
          onClick={handleCopy}
          className="text-xs text-slate-600 hover:text-slate-800 flex items-center gap-1"
        >
          {copied ? <CheckIcon className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Kopiert' : 'Kopieren'}
        </button>
      </div>
      <pre 
        className="p-3 text-xs font-mono text-slate-700 whitespace-pre-wrap overflow-x-auto overflow-y-auto"
        style={{ maxHeight }}
      >
        {content}
      </pre>
    </div>
  );
}

// NetworkEventDetails: Rendert interface_configured und network_rollback Events schoen
function NetworkEventDetails({ 
  details, 
  event 
}: { 
  details: Record<string, unknown>; 
  event: string;
}) {
  const [showRaw, setShowRaw] = useState(false);
  
  // Pruefe ob es ein Network-Event mit interfaces Array ist
  const interfaces = details.interfaces as Array<{
    name: string;
    changes: Array<{ field: string; from: string; to: string }>;
  }> | undefined;
  
  const isNetworkEvent = event === 'interface_configured' || event === 'network_rollback';
  const hasInterfaceDiffs = interfaces && interfaces.length > 0;
  
  if (!isNetworkEvent || !hasInterfaceDiffs) {
    // Fallback: Raw JSON anzeigen
    return (
      <div className="p-2 bg-slate-50 rounded border border-slate-200 font-mono">
        {Object.entries(details).map(([key, value]) => (
          <div key={key} className="text-slate-600">
            <span className="text-slate-400">{key}: </span>
            <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
          </div>
        ))}
      </div>
    );
  }
  
  const reason = details.reason as string | undefined;
  const rolledBackTo = details.rolledBackTo as string | undefined;
  const connectivity = details.connectivity as { ok: boolean; retries?: number } | undefined;
  
  return (
    <div className="space-y-2">
      {/* Rollback Reason */}
      {event === 'network_rollback' && reason && (
        <div className="flex items-center gap-2 text-red-600 font-medium">
          <AlertTriangle className="w-3.5 h-3.5" />
          Rollback: {reason === 'connectivity_failed' ? 'Verbindung verloren' : reason}
          {rolledBackTo && <span className="text-slate-400 font-normal text-xs">(Hash: {rolledBackTo.slice(0, 8)})</span>}
        </div>
      )}
      
      {/* Interface Diffs */}
      <div className="p-2 bg-slate-50 rounded border border-slate-200">
        {interfaces.map((iface) => (
          <div key={iface.name} className="mb-2 last:mb-0">
            <div className="font-medium text-slate-700">{iface.name}:</div>
            <div className="ml-3 space-y-0.5">
              {iface.changes.map((change, idx) => (
                <div key={idx} className="text-slate-600">
                  <span className="text-slate-500">{change.field}:</span>{' '}
                  <span className="text-red-500 line-through">{change.from || '-'}</span>
                  {' → '}
                  <span className="text-green-600 font-medium">{change.to || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Connectivity Status */}
      {connectivity && (
        <div className={`text-xs ${connectivity.ok ? 'text-green-600' : 'text-red-600'}`}>
          Connectivity: {connectivity.ok ? 'OK' : 'Failed'}
          {connectivity.retries !== undefined && connectivity.retries > 0 && ` (${connectivity.retries} Retries)`}
        </div>
      )}
      
      {/* Raw Payload Toggle */}
      <button
        onClick={() => setShowRaw(!showRaw)}
        className="text-xs text-slate-400 hover:text-slate-600"
      >
        {showRaw ? 'Raw ausblenden' : 'Raw anzeigen'}
      </button>
      {showRaw && (
        <pre className="p-2 bg-slate-100 rounded text-xs font-mono overflow-x-auto">
          {JSON.stringify(details, null, 2)}
        </pre>
      )}
    </div>
  );
}

const COMMANDS = [
  { id: 'send_logs_100', label: 'Logs abrufen', icon: FileText },
  { id: 'restart_agent', label: 'Agent neu starten', icon: RotateCcw },
  { id: 'agent_update', label: 'Agent aktualisieren', icon: ArrowUpCircle, highlight: true },
  { id: 'system_update', label: 'System Update', icon: Zap },
  { id: 'reboot', label: 'Reboot', icon: Terminal },
] as const;

const COMMAND_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  EXECUTING: 'bg-blue-100 text-blue-800',
  SUCCESS: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  REJECTED: 'bg-slate-100 text-slate-800',
  TIMEOUT: 'bg-orange-100 text-orange-800',
};

const STATUS_CONFIG: Record<DeviceStatus, { label: string; color: string; icon: typeof Clock }> = {
  PENDING: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  APPROVED: { label: 'Freigegeben', color: 'bg-green-100 text-green-800', icon: CircleCheck },
  REJECTED: { label: 'Abgelehnt', color: 'bg-red-100 text-red-800', icon: CircleSlash },
  BANNED: { label: 'Wartet auf Bestätigung des Geräts', color: 'bg-orange-100 text-orange-700', icon: Loader2 },
  DECOMMISSIONED: { label: 'Entfernt', color: 'bg-slate-100 text-slate-400', icon: Trash2 },
};

const ROLE_OPTIONS: { value: DeviceRole; label: string }[] = [
  { value: 'GENERIC', label: 'Generic (Keine Rolle)' },
  { value: 'TWA', label: 'Taktwaschanlage (TWA)' },
  { value: 'WATER_METER', label: 'Wasserzahler' },
  { value: 'CONSUMABLES', label: 'Verbrauchsmaterial' },
];

const TWA_DEFAULTS = {
  smbHost: '192.168.10.50',
  smbShare: 'VisuWasch',
};

const TWA_MACHINE_DEFS = [
  { code: 'TWA_P50' as const, name: '50kg Taktwaschanlage', basePath: 'CBW1/SavedData/ShiftChange' },
  { code: 'TWA_P25' as const, name: '25kg Taktwaschanlage', basePath: 'CBW2/SavedData/ShiftChange' },
];

// Muss zu TWA_FEED_IDS in role-config.schema.ts passen - die Steuerung legt je
// Schichtwechsel genau diese sieben Ordner an.
const TWA_ALL_FEEDS = [
  'maschinendaten',
  'program_gewicht',
  'kunde_gewicht',
  'frischwasser',
  'waschmittel',
  'stoermeldungen',
  'warnungen',
];


function RoleConfigEditor({
  device,
  onSave,
  saving,
}: {
  device: IngestDevice;
  onSave: (role: DeviceRole, config: TwaRoleConfig | null) => Promise<boolean>;
  saving: boolean;
}) {
  const [role, setRole] = useState<DeviceRole>(device.role || 'GENERIC');
  const [smbHost, setSmbHost] = useState<string>(device.configJson?.smbHost || TWA_DEFAULTS.smbHost);
  const [smbShare, setSmbShare] = useState<string>(device.configJson?.smbShare || TWA_DEFAULTS.smbShare);
  const [smbUsername, setSmbUsername] = useState<string>(device.configJson?.smbUsername || '');
  const [smbPassword, setSmbPassword] = useState<string>(device.configJson?.smbPassword || '');
  const [enabledMachines, setEnabledMachines] = useState<Set<string>>(() => {
    const existing = device.configJson?.machines?.map(m => m.machineCode) || [];
    return new Set(existing.length > 0 ? existing : TWA_MACHINE_DEFS.map(m => m.code));
  });
  const [saved, setSaved] = useState(false);

  const toggleMachine = (code: string) => {
    setEnabledMachines(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSave = async () => {
    let success = false;
    if (role === 'GENERIC') {
      success = await onSave(role, null);
    } else if (role === 'TWA') {
      const machines: TwaMachine[] = TWA_MACHINE_DEFS
        .filter(m => enabledMachines.has(m.code))
        .map(m => ({
          machineCode: m.code,
          displayName: m.name,
          basePath: m.basePath,
          feeds: TWA_ALL_FEEDS,
        }));
      success = await onSave(role, { 
        smbHost, 
        smbShare,
        smbUsername: smbUsername || undefined,
        smbPassword: smbPassword || undefined,
        machines 
      });
    }
    if (success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const configApplied = device.configVersion > 0 && device.lastConfigAppliedVersion === device.configVersion;
  const configPending = device.configVersion > 0 && (device.lastConfigAppliedVersion ?? 0) < device.configVersion;

  const getConfigStatus = () => {
    if (device.configVersion === 0) return null;
    if (configApplied) return { label: 'Angewendet', color: 'bg-green-100 text-green-700' };
    if (configPending) return { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-700' };
    return null;
  };

  const status = getConfigStatus();

  const formatConfigDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Settings className="w-4 h-4" />
          Rollenkonfiguration
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">
            v{device.configVersion}
            {device.lastConfigAppliedVersion !== null && device.lastConfigAppliedVersion !== undefined && (
              <span className="text-slate-400"> (Agent: v{device.lastConfigAppliedVersion})</span>
            )}
          </span>
          {status && (
            <span className={`px-1.5 py-0.5 rounded ${status.color}`}>
              {status.label}
            </span>
          )}
        </div>
      </div>

      {/* Config Timestamp Info */}
      {device.configVersion > 0 && device.configUpdatedAt && (
        <div className="text-xs text-slate-400">
          Konfiguriert: {formatConfigDate(device.configUpdatedAt)}
        </div>
      )}

      {/* Role Dropdown */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Rolle</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as DeviceRole)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {ROLE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* TWA Config */}
      {role === 'TWA' && (
        <div className="space-y-4 pt-2 border-t border-slate-200">
          {/* SMB Connection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">XP-Rechner IP</label>
              <input
                type="text"
                value={smbHost}
                onChange={(e) => setSmbHost(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="192.168.10.50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Share</label>
              <input
                type="text"
                value={smbShare}
                onChange={(e) => setSmbShare(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="VisuWasch"
              />
            </div>
          </div>

          {/* Machines */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Anlagen</label>
            <div className="space-y-2">
              {TWA_MACHINE_DEFS.map(m => (
                <label key={m.code} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${enabledMachines.has(m.code) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={enabledMachines.has(m.code)}
                    onChange={() => toggleMachine(m.code)}
                    className="rounded border-slate-300"
                  />
                  <div>
                    <div className="text-sm font-medium">{m.name}</div>
                    <div className="text-xs text-slate-500 font-mono">\\{smbHost}\{smbShare}\{m.basePath}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Credentials (optional) */}
          <details className="text-xs">
            <summary className="text-slate-500 cursor-pointer hover:text-slate-700">Zugangsdaten (optional)</summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="block text-slate-500 mb-1">Username</label>
                <input
                  type="text"
                  value={smbUsername}
                  onChange={(e) => setSmbUsername(e.target.value)}
                  className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                  placeholder="leer = anonym"
                />
              </div>
              <div>
                <label className="block text-slate-500 mb-1">Password</label>
                <input
                  type="password"
                  value={smbPassword}
                  onChange={(e) => setSmbPassword(e.target.value)}
                  className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                />
              </div>
            </div>
          </details>

          {/* Feeds Info */}
          <div className="text-xs text-slate-500 bg-slate-100 rounded p-2">
            <span className="font-medium">Daten je Anlage:</span> Anlagendaten, Programm-Gewicht, Kunden-Gewicht, Frischwasser, Störmeldungen, Warnungen
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="pt-2 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || (role === 'TWA' && enabledMachines.size === 0)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Konfiguration speichern
        </button>
        {saved && (
          <span className="text-sm text-green-600 font-medium flex items-center gap-1">
            <CircleCheck className="w-4 h-4" />
            Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}

export default function IngestDevicesPage() {
  const [devices, setDevices] = useState<IngestDevice[]>([]);
  const [loading, setLoading] = useState(true);
  // Erstladung von Hintergrund-Refresh trennen (sonst blinkt die Liste alle 10 s)
  const devicesLoadedOnce = useRef(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  // In-App statt confirm()/alert() (native Dialoge sind projektweit unerwuenscht)
  const [artifactDeleteId, setArtifactDeleteId] = useState<string | null>(null);
  const [roleConfigError, setRoleConfigError] = useState<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [forceDecommissionOpen, setForceDecommissionOpen] = useState(false);
  const [forceDecommissionLoading, setForceDecommissionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | ''>('');
  const [pendingCount, setPendingCount] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<IngestDevice | null>(null);
  const [commandHistory, setCommandHistory] = useState<Record<string, IngestCommand[]>>({});
  const [commandLoading, setCommandLoading] = useState<string | null>(null);
  const [expandedCommand, setExpandedCommand] = useState<string | null>(null);
  const [roleConfigSaving, setRoleConfigSaving] = useState<string | null>(null);
  
  // Tab State for Device Detail Modal
  type DetailTab = 'system' | 'network' | 'role' | 'commands' | 'logs';
  const [activeTab, setActiveTab] = useState<DetailTab>('system');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [logFilter, setLogFilter] = useState<string | null>(null);

  // Interface Role Dialog State
  const [editingInterface, setEditingInterface] = useState<InterfaceInfo | null>(null);
  const [interfaceRoleForm, setInterfaceRoleForm] = useState<{
    role: InterfaceRole;
    purpose: string;
    method: 'dhcp' | 'static';
    ipAddress: string;
    gateway: string;
    dns: string;
    metricPreset: 'auto' | 'preferred' | 'standard' | 'fallback' | 'custom';
    metricCustom: string;
  }>({ role: 'uplink', purpose: '', method: 'dhcp', ipAddress: '', gateway: '', dns: '', metricPreset: 'auto', metricCustom: '' });
  const [interfaceConfigSaving, setInterfaceConfigSaving] = useState(false);
  const [showUplinkWarning, setShowUplinkWarning] = useState(false);
  const [uplinkWarningConfirmed, setUplinkWarningConfirmed] = useState(false);
  const [ipValidationError, setIpValidationError] = useState<string | null>(null);

  // Ban Device Dialog State
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banLoading, setBanLoading] = useState(false);

  // Join Code Dialog State
  const [showJoinCodeDialog, setShowJoinCodeDialog] = useState(false);
  const [joinCode, setJoinCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [joinCodeLoading, setJoinCodeLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<'code' | 'server' | null>(null);

  // Rename State
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // Agent Events State (mit Kategorien-Filter)
  const [agentEvents, setAgentEvents] = useState<{
    events: Array<{ ts: string; level: string; event: string; category: string; msg: string; details?: Record<string, unknown> }>;
    oldestLogAt: string | null;
    newestLogAt: string | null;
    truncated: boolean;
  } | null>(null);
  const [agentEventsLoading, setAgentEventsLoading] = useState(false);
  const [agentEventsError, setAgentEventsError] = useState<string | null>(null);
  
  // Filter State für Logs-Tab
  const AVAILABLE_CATEGORIES = ['ingest', 'network', 'commands', 'config', 'health', 'lifecycle'] as const;
  const CATEGORY_LABELS: Record<string, string> = {
    ingest: 'Ingest',
    network: 'Netzwerk',
    commands: 'Commands',
    config: 'Config',
    health: 'Health',
    lifecycle: 'Lifecycle',
  };
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set(AVAILABLE_CATEGORIES));
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set(['info', 'warn', 'error']));

  // Artifact Management State
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [artifacts, setArtifacts] = useState<AgentArtifact[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactActionLoading, setArtifactActionLoading] = useState<string | null>(null);
  const [artifactMessage, setArtifactMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ arch: 'arm64', channel: 'stable', version: '' });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Lade Agent-Events aus IngestHealthLog (mit Kategorien-Filter)
  const loadAgentEvents = async (deviceId: string, categories?: string[], levels?: string[]) => {
    setAgentEventsLoading(true);
    setAgentEventsError(null);
    try {
      const params = new URLSearchParams({ hoursBack: '24', limit: '500' });
      if (categories && categories.length > 0 && categories.length < AVAILABLE_CATEGORIES.length) {
        params.set('categories', categories.join(','));
      }
      if (levels && levels.length > 0 && levels.length < 3) {
        params.set('levels', levels.join(','));
      }
      const res = await fetch(`/api/admin/ingest-devices/${deviceId}/ingest-events?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAgentEvents(data);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setAgentEventsError(errorData.message || `Fehler ${res.status}`);
        setAgentEvents(null);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Agent-Events:', err);
      setAgentEventsError('Netzwerkfehler');
      setAgentEvents(null);
    }
    setAgentEventsLoading(false);
  };

  const loadArtifacts = async () => {
    setArtifactsLoading(true);
    try {
      const res = await fetch('/api/admin/agent-artifacts', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setArtifacts(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Artifacts:', err);
    }
    setArtifactsLoading(false);
  };

  const handleRegisterLocal = async () => {
    setArtifactActionLoading('register');
    setArtifactMessage(null);
    try {
      const res = await fetch('/api/admin/agent-artifacts/register-local', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        await loadArtifacts();
        setArtifactMessage({ type: 'success', text: `v${data.version} registriert` });
      } else {
        setArtifactMessage({ type: 'error', text: data.message || 'Registrierung fehlgeschlagen' });
      }
    } catch (err) {
      setArtifactMessage({ type: 'error', text: 'Netzwerkfehler' });
    }
    setArtifactActionLoading(null);
  };

  const handleUploadArtifact = async () => {
    if (!uploadFile || !uploadForm.version.match(/^\d+\.\d+\.\d+$/)) {
      setArtifactMessage({ type: 'error', text: 'Datei und Version (x.y.z) erforderlich' });
      return;
    }
    setUploadLoading(true);
    setArtifactMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await fetch(
        `/api/admin/agent-artifacts/upload?arch=${uploadForm.arch}&channel=${uploadForm.channel}&version=${uploadForm.version}`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      const data = await res.json();
      if (res.ok) {
        await loadArtifacts();
        setShowUploadDialog(false);
        setUploadFile(null);
        setUploadForm({ arch: 'arm64', channel: 'stable', version: '' });
        setArtifactMessage({ type: 'success', text: `v${data.version} hochgeladen` });
      } else {
        setArtifactMessage({ type: 'error', text: data.message || 'Upload fehlgeschlagen' });
      }
    } catch (err) {
      setArtifactMessage({ type: 'error', text: 'Netzwerkfehler' });
    }
    setUploadLoading(false);
  };

  const handleActivateArtifact = async (id: string) => {
    setArtifactActionLoading(id);
    try {
      const res = await fetch(`/api/admin/agent-artifacts/${id}/activate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await loadArtifacts();
      }
    } catch (err) {
      console.error('Fehler:', err);
    }
    setArtifactActionLoading(null);
  };

  const handleDeleteArtifact = async (id: string) => {
    setArtifactActionLoading(id);
    setArtifactMessage(null);
    try {
      const res = await fetch(`/api/admin/agent-artifacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        await loadArtifacts();
        setArtifactMessage({ type: 'success', text: 'Artifact geloescht' });
      } else {
        const data = await res.json();
        setArtifactMessage({ type: 'error', text: data.message || 'Loeschen fehlgeschlagen' });
      }
    } catch (err) {
      setArtifactMessage({ type: 'error', text: 'Netzwerkfehler' });
    }
    setArtifactActionLoading(null);
  };

  const loadDevices = async () => {
    // Nur bei der Erstladung den Ladezustand zeigen. Der 10-Sekunden-Poll
    // liess sonst alle 10 s die komplette Liste verschwinden ("Blinken").
    if (devicesLoadedOnce.current === false) setLoading(true);
    try {
      const url = statusFilter
        ? `/api/admin/ingest-devices?status=${statusFilter}`
        : '/api/admin/ingest-devices';
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data: DevicesResponse = await res.json();
        setDevices(data.devices);
        setPendingCount(data.pendingCount);
        setDevicesError(null);
        // Update selectedDevice if it exists in the new data
        setSelectedDevice(prev => {
          if (!prev) return null;
          const updated = data.devices.find(d => d.id === prev.id);
          return updated || prev;
        });
      } else {
        // Frueher stumm: bei Serverfehlern blieb die alte Liste stehen und
        // der Admin sah Geraete, die es so nicht mehr gibt.
        setDevicesError(`Geraeteliste konnte nicht geladen werden (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Geraete:', err);
      setDevicesError('Geraeteliste konnte nicht geladen werden - keine Verbindung zum Server.');
    }
    devicesLoadedOnce.current = true;
    setLoading(false);
  };

  useEffect(() => {
    loadDevices();
    const interval = setInterval(() => {
      loadDevices();
    }, 10000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const filtered = useMemo(() => {
    let result = devices;
    // Hide DECOMMISSIONED devices unless explicitly filtered
    if (statusFilter !== 'DECOMMISSIONED') {
      result = result.filter(d => d.status !== 'DECOMMISSIONED');
    }
    if (!search) return result;
    const s = search.toLowerCase();
    return result.filter(
      (d) =>
        d.deviceId.toLowerCase().includes(s) ||
        d.hardwareId?.toLowerCase().includes(s) ||
        d.piModel?.toLowerCase().includes(s)
    );
  }, [devices, search, statusFilter]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${id}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await loadDevices();
      }
    } catch (err) {
      console.error('Fehler beim Freigeben:', err);
    }
    setActionLoading(null);
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${id}/reject`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await loadDevices();
      }
    } catch (err) {
      console.error('Fehler beim Ablehnen:', err);
    }
    setActionLoading(null);
  };

  const handleSaveRoleConfig = async (deviceId: string, role: DeviceRole, roleConfig: TwaRoleConfig | null): Promise<boolean> => {
    setRoleConfigSaving(deviceId);
    setRoleConfigError(null);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${deviceId}/role-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role, roleConfig }),
      });
      if (res.ok) {
        await loadDevices();
        setRoleConfigSaving(null);
        return true;
      } else {
        const err = await res.json();
        console.error('Fehler beim Speichern der Rollenkonfiguration:', err.message);
        setRoleConfigError(err.message || 'Speichern fehlgeschlagen.');
        setRoleConfigSaving(null);
        return false;
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Rollenkonfiguration:', err);
      setRoleConfigSaving(null);
      return false;
    }
  };

  const loadCommandHistory = async (deviceId: string) => {
    try {
      const res = await fetch(`/api/admin/ingest-devices/${deviceId}/commands`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setCommandHistory((prev) => ({ ...prev, [deviceId]: data }));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Command-History:', err);
    }
  };

  const handleSendCommand = async (deviceId: string, command: string) => {
    setCommandLoading(`${deviceId}-${command}`);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${deviceId}/command`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command }),
      });
      if (res.ok) {
        await loadCommandHistory(deviceId);
      }
    } catch (err) {
      console.error('Fehler beim Senden des Commands:', err);
    }
    setCommandLoading(null);
  };

  // Geraete, die es physisch nicht mehr gibt, koennen die Entfernung nie
  // bestaetigen - sie blieben sonst dauerhaft auf "Wartet auf Bestaetigung".
  const handleForceDecommission = async (deviceId: string) => {
    setForceDecommissionLoading(true);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${deviceId}/force-decommission`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setForceDecommissionOpen(false);
        closeDrawer();
        await loadDevices();
      } else {
        const data = await res.json().catch(() => ({}));
        setDevicesError(data.message || 'Gerät konnte nicht außer Betrieb gesetzt werden.');
      }
    } catch {
      setDevicesError('Gerät konnte nicht außer Betrieb gesetzt werden - keine Verbindung.');
    }
    setForceDecommissionLoading(false);
  };

  const openDeviceDetail = (device: IngestDevice) => {
    setSelectedDevice(device);
    // Immer neu laden: Der Verlauf wurde frueher nur beim allerersten Oeffnen
    // geholt - Ergebnisse abgeschickter Befehle sah man erst nach einem
    // kompletten Seiten-Reload.
    loadCommandHistory(device.id);
  };

  // Solange ein Befehl offen ist, den Verlauf automatisch nachladen - sonst
  // sieht man das Ergebnis erst nach einem kompletten Seiten-Reload.
  useEffect(() => {
    if (!selectedDevice || activeTab !== 'commands') return;
    const offen = commandHistory[selectedDevice.id]?.some(
      c => c.status === 'PENDING' || c.status === 'EXECUTING',
    );
    if (!offen) return;
    const timer = setInterval(() => loadCommandHistory(selectedDevice.id), 5000);
    return () => clearInterval(timer);
  }, [selectedDevice?.id, activeTab, commandHistory]);

  const closeDrawer = () => {
    setSelectedDevice(null);
    setAgentEvents(null); // Reset für nächstes Öffnen
    setAgentEventsError(null);
  };

  // Interface Role Management
  const openInterfaceRoleDialog = (iface: InterfaceInfo) => {
    setEditingInterface(iface);
    // Initialize form with SAVED WebApp config if exists, otherwise current device state
    const savedConfig = selectedDevice?.interfaceConfig?.[iface.name];
    // Metric: null/undefined => auto, >0 => check presets
    const savedMetric = savedConfig?.metric;
    let metricPreset: 'auto' | 'preferred' | 'standard' | 'fallback' | 'custom' = 'auto';
    let metricCustom = '';
    if (savedMetric === 10) metricPreset = 'preferred';
    else if (savedMetric === 100) metricPreset = 'standard';
    else if (savedMetric === 200) metricPreset = 'fallback';
    else if (savedMetric && savedMetric > 0) { metricPreset = 'custom'; metricCustom = String(savedMetric); }
    
    setInterfaceRoleForm({ 
      role: savedConfig?.role || iface.role || 'uplink', 
      purpose: savedConfig?.purpose || '',
      method: savedConfig?.method || (iface.dhcpOrStatic === 'static' ? 'static' : 'dhcp'),
      ipAddress: savedConfig?.ipAddress || iface.ipv4Addrs?.[0] || '',
      gateway: savedConfig?.gateway || iface.gateway || '',
      dns: savedConfig?.dns?.join(', ') || iface.dns?.join(', ') || '',
      metricPreset,
      metricCustom,
    });
    setShowUplinkWarning(false);
    setUplinkWarningConfirmed(false);
  };

  const closeInterfaceRoleDialog = () => {
    setEditingInterface(null);
    setShowUplinkWarning(false);
    setUplinkWarningConfirmed(false);
  };

  const handleInterfaceRoleSave = async () => {
    if (!selectedDevice || !editingInterface) return;
    
    // Check if editing active uplink and changing to isolated
    if (editingInterface.isActiveUplink && interfaceRoleForm.role === 'isolated' && !uplinkWarningConfirmed) {
      setShowUplinkWarning(true);
      return;
    }

    // Validate static IP has CIDR notation
    if (interfaceRoleForm.method === 'static') {
      const ipCidrRegex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
      if (!ipCidrRegex.test(interfaceRoleForm.ipAddress)) {
        setIpValidationError('CIDR-Notation erforderlich (z.B. /24)');
        return;
      }
    }
    setIpValidationError(null);

    setInterfaceConfigSaving(true);
    try {
      // Metric berechnen: auto => null, preset => Wert, custom => parsed
      let metric: number | null | undefined = undefined;
      if (interfaceRoleForm.role === 'uplink') {
        if (interfaceRoleForm.metricPreset === 'auto') metric = null;
        else if (interfaceRoleForm.metricPreset === 'preferred') metric = 10;
        else if (interfaceRoleForm.metricPreset === 'standard') metric = 100;
        else if (interfaceRoleForm.metricPreset === 'fallback') metric = 200;
        else if (interfaceRoleForm.metricPreset === 'custom') {
          const parsed = parseInt(interfaceRoleForm.metricCustom, 10);
          metric = (parsed > 0 && parsed < 10000) ? parsed : null;
        }
      }
      
      const res = await fetch(`/api/admin/ingest-devices/${selectedDevice.id}/interface-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          interfaceName: editingInterface.name,
          mac: editingInterface.mac,
          role: interfaceRoleForm.role,
          purpose: interfaceRoleForm.purpose || undefined,
          method: interfaceRoleForm.method,
          ipAddress: interfaceRoleForm.method === 'static' ? interfaceRoleForm.ipAddress : undefined,
          gateway: interfaceRoleForm.method === 'static' ? interfaceRoleForm.gateway : undefined,
          dns: interfaceRoleForm.method === 'static' && interfaceRoleForm.dns 
            ? interfaceRoleForm.dns.split(',').map(s => s.trim()).filter(Boolean) 
            : undefined,
          metric: interfaceRoleForm.role === 'uplink' ? metric : undefined,
        }),
      });
      if (res.ok) {
        await loadDevices();
        closeInterfaceRoleDialog();
      } else {
        console.error('Fehler beim Speichern der Interface-Konfiguration');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Interface-Konfiguration:', err);
    }
    setInterfaceConfigSaving(false);
  };

  const handleBanDevice = async () => {
    if (!selectedDevice) return;
    setBanLoading(true);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${selectedDevice.id}/ban`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: banReason || undefined }),
      });
      if (res.ok) {
        await loadDevices();
        setShowBanDialog(false);
        setBanReason('');
        setSelectedDevice(null);
      } else {
        console.error('Fehler beim Entfernen des Geräts');
      }
    } catch (err) {
      console.error('Fehler beim Entfernen des Geräts:', err);
    }
    setBanLoading(false);
  };

  const handleRenameDevice = async () => {
    setRenameError(null);
    if (!selectedDevice || !editedName.trim()) return;
    setRenameLoading(true);
    try {
      const res = await fetch(`/api/admin/ingest-devices/${selectedDevice.id}/rename`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editedName.trim() }),
      });
      if (res.ok) {
        await loadDevices();
        setIsEditingName(false);
      } else {
        const data = await res.json();
        setRenameError(data.message || 'Umbenennen fehlgeschlagen.');
      }
    } catch (err) {
      console.error('Fehler beim Umbenennen:', err);
    }
    setRenameLoading(false);
  };

  const handleGenerateJoinCode = async () => {
    setJoinCodeLoading(true);
    try {
      const res = await fetch('/api/admin/ingest-devices/generate-join-code', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setJoinCode(data);
        setShowJoinCodeDialog(true);
      } else {
        console.error('Fehler beim Generieren des Join-Codes');
      }
    } catch (err) {
      console.error('Fehler beim Generieren des Join-Codes:', err);
    }
    setJoinCodeLoading(false);
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'gerade eben';
    if (mins < 60) return `vor ${mins} Min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours}h`;
    const days = Math.floor(hours / 24);
    return `vor ${days}d`;
  };

  // Der Verbindungszustand kommt jetzt vom Server (eine Wahrheit fuer alle
  // Ansichten). Der Zweig ohne Serverwert deckt nur aeltere Backend-Staende ab.
  const getEffectiveRunnerState = (device: IngestDevice): EffectiveRunnerState => {
    if (device.effectiveRunnerState) return device.effectiveRunnerState as EffectiveRunnerState;
    return device.lastHealthPayload?.role?.runnerState ?? 'unknown';
  };

  const CONNECTIVITY_LABEL: Record<string, { text: string; className: string }> = {
    online: { text: 'erreichbar', className: 'text-green-600' },
    stale: { text: 'verzögert', className: 'text-amber-600' },
    offline: { text: 'nicht erreichbar', className: 'text-red-600' },
    never_seen: { text: 'nie gesehen', className: 'text-slate-400' },
  };

  const getRunnerStateClassName = (state: EffectiveRunnerState) => {
    if (state === 'running') return 'text-green-600';
    if (state === 'error' || state === 'offline') return 'text-red-600';
    if (state === 'unknown') return 'text-slate-400';
    return 'text-yellow-600';
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Cpu className="w-6 h-6" />
              Ingest Devices
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} von {devices.length} Geraeten
              {pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                  {pendingCount} ausstehend
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateJoinCode}
              disabled={joinCodeLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            >
              {joinCodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Neues Gerät
            </button>
            <button
              onClick={loadDevices}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Suchen (Device ID, MAC, Model)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DeviceStatus | '')}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Alle Status</option>
            <option value="PENDING">Ausstehend</option>
            <option value="APPROVED">Freigegeben</option>
            <option value="REJECTED">Abgelehnt</option>
            <option value="BANNED">Wartet auf Bestätigung</option>
            <option value="DECOMMISSIONED">Entfernt</option>
          </select>
        </div>

        {forceDecommissionOpen && selectedDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Endgültig außer Betrieb setzen?</h2>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-slate-600">
                <strong>{selectedDevice.deviceId}</strong> wird als außer Betrieb geführt und
                aus der Liste ausgeblendet. Der Zugangsschlüssel wird entwertet.
              </p>
              <p className="text-sm text-slate-500">
                Der Eintrag bleibt erhalten, damit bereits erfasste Messdaten ihrem Gerät
                zugeordnet bleiben. Über den Status-Filter ist er weiterhin auffindbar.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setForceDecommissionOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleForceDecommission(selectedDevice.id)}
                disabled={forceDecommissionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {forceDecommissionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Außer Betrieb setzen
              </button>
            </div>
          </div>
        </div>
      )}

      {artifactDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Agent-Paket löschen?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                Das Paket wird dauerhaft entfernt.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Geräte, die diese Version noch beziehen sollen, können sie danach nicht mehr laden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setArtifactDeleteId(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  const id = artifactDeleteId;
                  setArtifactDeleteId(null);
                  if (id) handleDeleteArtifact(id);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {devicesError && (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-start gap-2 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
              <span>{devicesError} Angezeigt wird der letzte bekannte Stand.</span>
            </div>
            <button
              onClick={() => loadDevices()}
              className="shrink-0 rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-100"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {/* Agent Binary Management */}
        <div className="bg-white rounded-lg border">
          <button
            onClick={() => {
              setShowArtifacts(!showArtifacts);
              if (!showArtifacts && artifacts.length === 0) loadArtifacts();
            }}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50"
          >
            <div className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-slate-600" />
              <span className="font-medium text-slate-900">Agent Binaries</span>
              {artifacts.filter(a => a.isActive).length > 0 && (
                <span className="text-xs text-slate-500">
                  (Aktiv: {artifacts.filter(a => a.isActive).map(a => `${a.arch} v${a.version}`).join(', ')})
                </span>
              )}
            </div>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showArtifacts ? 'rotate-180' : ''}`} />
          </button>
          
          {showArtifacts && (
            <div className="border-t px-4 py-4 space-y-4">
              {/* Status Message */}
              {artifactMessage && (
                <div className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${artifactMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <span>{artifactMessage.text}</span>
                  <button onClick={() => setArtifactMessage(null)} className="ml-2 hover:opacity-70"><X className="w-4 h-4" /></button>
                </div>
              )}
              {/* Register Local / Upload Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleRegisterLocal}
                  disabled={artifactActionLoading === 'register'}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  title="Binary aus devices/ingest-agent/bin/ registrieren (Version aus VERSION Datei)"
                >
                  {artifactActionLoading === 'register' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
                  Lokales Binary registrieren
                </button>
                <button
                  onClick={() => setShowUploadDialog(true)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 flex items-center gap-2 text-sm"
                >
                  <ArrowUpCircle className="w-4 h-4" />
                  Externes Binary hochladen
                </button>
              </div>

              {/* Artifacts List */}
              {artifactsLoading ? (
                <div className="text-center py-4 text-slate-500">Laden...</div>
              ) : artifacts.length === 0 ? (
                <div className="text-center py-4 text-slate-500">Keine Artifacts vorhanden</div>
              ) : (
                <div className="space-y-2">
                  {artifacts.map((artifact) => (
                    <div
                      key={artifact.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${artifact.isActive ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        {artifact.isActive && <CircleCheck className="w-5 h-5 text-green-600" />}
                        <div>
                          <div className="font-medium text-sm">
                            {artifact.arch}/{artifact.channel} v{artifact.version}
                          </div>
                          <div className="text-xs text-slate-500">
                            {(artifact.size / 1024 / 1024).toFixed(1)} MB | SHA256: {artifact.sha256.slice(0, 12)}...
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!artifact.isActive && (
                          <>
                            <button
                              onClick={() => handleActivateArtifact(artifact.id)}
                              disabled={artifactActionLoading === artifact.id}
                              className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm disabled:opacity-50"
                            >
                              {artifactActionLoading === artifact.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktivieren'}
                            </button>
                            <button
                              onClick={() => setArtifactDeleteId(artifact.id)}
                              disabled={artifactActionLoading === artifact.id}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {artifact.isActive && (
                          <span className="text-xs text-green-700 font-medium">Aktiv</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upload Dialog */}
        {showUploadDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Agent Binary hochladen</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Datei</label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Version</label>
                    <input
                      type="text"
                      placeholder="x.y.z"
                      value={uploadForm.version}
                      onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Arch</label>
                    <select
                      value={uploadForm.arch}
                      onChange={(e) => setUploadForm({ ...uploadForm, arch: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="arm64">arm64</option>
                      <option value="amd64">amd64</option>
                      <option value="arm">arm</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Channel</label>
                    <select
                      value={uploadForm.channel}
                      onChange={(e) => setUploadForm({ ...uploadForm, channel: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="stable">stable</option>
                      <option value="beta">beta</option>
                      <option value="dev">dev</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowUploadDialog(false); setUploadFile(null); }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleUploadArtifact}
                  disabled={uploadLoading || !uploadFile}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Hochladen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Device Cards Grid */}
        {loading ? (
          <div className="p-8 text-center text-slate-500">Laden...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 bg-white rounded-lg border">
            {search || statusFilter ? 'Keine Treffer' : 'Keine Geraete registriert'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((device) => {
              const statusConfig = STATUS_CONFIG[device.status as DeviceStatus];
              const StatusIcon = statusConfig.icon;
              const isLoading = actionLoading === device.id;
              const health = device.lastHealthPayload;
              const roleStatus = health?.role;
              const configPending = device.configVersion > 0 && (device.lastConfigAppliedVersion ?? 0) < device.configVersion;

              return (
                <div
                  key={device.id}
                  // Detailansicht fuer alle Zustaende ausser PENDING - dort gibt
                  // es eigene Knoepfe direkt auf der Karte. Vorher liess sich nur
                  // ein freigegebenes Geraet oeffnen, wodurch ein gesperrtes
                  // Geraet gar nicht mehr zu erreichen war.
                  className={`bg-white rounded-lg border p-4 ${
                    device.status !== 'PENDING'
                      ? 'cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all'
                      : ''
                  }`}
                  onClick={() => device.status !== 'PENDING' && openDeviceDetail(device)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{device.deviceId}</div>
                      {device.piModel && (
                        <div className="text-xs text-slate-500 truncate">{device.piModel}</div>
                      )}
                    </div>
                  </div>
                  {device.status !== 'APPROVED' && (
                    <div className="mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </div>
                  )}

                  {/* Status Indicators for Approved Devices */}
                  {device.status === 'APPROVED' && (() => {
                    const wlanIf = health?.network?.interfaces?.find(i => i.type === 'wlan' && i.linkUp);
                    return (
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      {health?.agentVersion && (
                        <span className="font-mono text-slate-500">v{health.agentVersion}</span>
                      )}
                      {wlanIf ? (
                        <span className="flex items-center gap-1 text-green-600" title={`WLAN verbunden (${wlanIf.signalDbm} dBm)`}>
                          <Wifi className="w-3 h-3" />
                          {wlanIf.signalDbm}dBm
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400" title="Kein WLAN (Kabelverbindung)">
                          <WifiOff className="w-3 h-3" />
                        </span>
                      )}
                      {/* VPN immer anzeigen - vorher fehlte das Symbol bei
                          getrennter Verbindung komplett, man sah also nicht,
                          ob "kein VPN" oder "nicht gemeldet" gemeint war. */}
                      {health?.vpn?.netbirdConnected ? (
                        <span
                          className="flex items-center gap-1 text-green-600"
                          title={`NetBird verbunden${health.vpn.netbirdIp ? ` (${health.vpn.netbirdIp.split('/')[0]})` : ''}`}
                        >
                          <Globe className="w-3 h-3" />
                          VPN
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400" title="NetBird nicht verbunden">
                          <Globe className="w-3 h-3" />
                          <span className="line-through">VPN</span>
                        </span>
                      )}
                      {device.role !== 'GENERIC' && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{device.role}</span>
                      )}
                      {roleStatus?.runnerState && (
                        <span className={`${
                          roleStatus.runnerState === 'running' ? 'text-green-600' :
                          roleStatus.runnerState === 'error' ? 'text-red-600' : 'text-yellow-600'
                        }`}>{roleStatus.runnerState}</span>
                      )}
                      {configPending && (
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">!</span>
                      )}
                      <span className="text-slate-400 ml-auto">{device.lastSeenAt ? formatTimeAgo(device.lastSeenAt) : '-'}</span>
                    </div>
                    );
                  })()}

                  {/* Actions for Pending Devices */}
                  {device.status === 'PENDING' && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleApprove(device.id); }}
                        disabled={isLoading}
                        className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Freigeben
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReject(device.id); }}
                        disabled={isLoading}
                        className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Ablehnen
                      </button>
                    </div>
                  )}

                  {/* Hinweis, dass sich die Karte oeffnen laesst */}
                  {device.status !== 'PENDING' && (
                    <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
                      <span>
                        {device.status === 'BANNED'
                          ? 'Öffnen, um endgültig außer Betrieb zu setzen'
                          : device.lastSeenAt
                            ? `zuletzt ${formatTimeAgo(device.lastSeenAt)}`
                            : ''}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal - Centered like other entities */}
      {selectedDevice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeDrawer} />
          
          <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
            {/* HEADER */}
            <div className="p-5 border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <div>
                  {isEditingName ? (
                    <div className="flex flex-col gap-1">
                     <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameDevice();
                          if (e.key === 'Escape') setIsEditingName(false);
                        }}
                        className="text-xl font-bold text-slate-900 border border-blue-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={handleRenameDevice}
                        disabled={renameLoading}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                      >
                        {renameLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckIcon className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => { setIsEditingName(false); setRenameError(null); }}
                        className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                      >
                        <X className="w-5 h-5" />
                      </button>
                     </div>
                     {renameError && (
                       <span className="text-xs text-red-600">{renameError}</span>
                     )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-slate-900">{selectedDevice.deviceId}</h2>
                      <button
                        onClick={() => {
                          setEditedName(selectedDevice.deviceId);
                          setIsEditingName(true);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                        title="Umbenennen"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-500 mt-1 flex-wrap">
                    <span>{selectedDevice.lastOsRelease || 'OS unbekannt'}</span>
                    <span className="text-slate-300">|</span>
                    <span>{selectedDevice.role === 'TWA' ? 'Taktwaschanlage' : selectedDevice.role}</span>
                    {/* Letzter Kontakt gehoert nach oben: Alle Werte im Dialog
                        stammen aus dem letzten Health-Report - ohne dessen
                        Alter sind Temperatur, Last und IPs nicht einzuordnen. */}
                    <span className="text-slate-300">|</span>
                    {(() => {
                      const zustand = selectedDevice.connectivityStatus;
                      const label = zustand ? CONNECTIVITY_LABEL[zustand] : null;
                      return (
                        <span className={`font-medium ${label?.className ?? 'text-slate-500'}`}>
                          {label?.text ?? 'Zustand unbekannt'}
                          {selectedDevice.lastSeenAt && (
                            <span className="font-normal text-slate-500">
                              {' '}· letzter Kontakt {formatTimeAgo(selectedDevice.lastSeenAt)}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  {selectedDevice.status === 'BANNED' && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
                      <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
                      <span>
                        Das Entfernen wartet darauf, dass sich das Gerät meldet und die
                        Löschung bestätigt. Existiert das Gerät nicht mehr, kommt diese
                        Bestätigung nie — dann „Endgültig außer Betrieb" wählen.
                      </span>
                    </div>
                  )}
                  {(selectedDevice.connectivityStatus === 'offline' ||
                    selectedDevice.connectivityStatus === 'never_seen') && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
                      <span>
                        Das Gerät meldet sich nicht. Die folgenden Werte stammen aus dem
                        letzten empfangenen Bericht und können veraltet sein.
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedDevice.status === 'BANNED' && (
                    <button
                      onClick={() => setForceDecommissionOpen(true)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Endgültig außer Betrieb
                    </button>
                  )}
                  {selectedDevice.status === 'APPROVED' && (
                    <button 
                      onClick={() => setShowBanDialog(true)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Entfernen
                    </button>
                  )}
                  <button onClick={closeDrawer} className="p-2 text-slate-500 hover:bg-slate-200 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* TAB NAVIGATION */}
              <div className="flex gap-1 mt-4 -mb-5 px-1">
                {([
                  { id: 'system', label: 'System', icon: Cpu },
                  { id: 'network', label: 'Netzwerk', icon: Network },
                  { id: 'role', label: 'Rolle', icon: Settings },
                  { id: 'commands', label: 'Commands', icon: Terminal },
                  { id: 'logs', label: 'Logs', icon: FileText },
                ] as const).map((tab) => {
                  const TabIcon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${
                        isActive 
                          ? 'bg-white text-slate-900 border-t border-l border-r border-slate-200' 
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <TabIcon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* TAB CONTENT - Fixed height to prevent jumping */}
            <div className="overflow-y-auto p-5" style={{ height: '60vh' }}>
              
              {/* SYSTEM TAB */}
              {activeTab === 'system' && (
                <div className="space-y-4">
                  {/* Betriebliche Kurzfassung: Was zaehlt, bevor man in die
                      Detailkacheln schaut - laeuft die Datenerfassung, ist die
                      Anlage erreichbar, kommt man aus der Ferne drauf? */}
                  {(() => {
                    const hp = selectedDevice.lastHealthPayload;
                    const rolle = hp?.role;
                    const anlageOk = rolle?.runnerState === 'running' && !rolle?.lastError;
                    const punkte: { label: string; wert: string; ok: boolean | null }[] = [
                      {
                        label: 'Datenerfassung',
                        wert: selectedDevice.role === 'GENERIC'
                          ? 'keine Rolle zugewiesen'
                          : anlageOk
                            ? `läuft · ${rolle?.feedsActive ?? 0} Datenquellen`
                            : (rolle?.lastError || getEffectiveRunnerState(selectedDevice)),
                        ok: selectedDevice.role === 'GENERIC' ? null : anlageOk,
                      },
                      {
                        label: 'Fernzugriff',
                        wert: hp?.vpn?.netbirdConnected
                          ? (hp.vpn.netbirdIp?.split('/')[0] ?? 'verbunden')
                          : 'nicht verbunden',
                        ok: hp?.vpn?.netbirdConnected ?? null,
                      },
                      {
                        label: 'Datenträger',
                        wert: hp?.storage?.wearPercent != null
                          ? `${hp.storage.wearPercent}% verbraucht`
                          : 'unbekannt',
                        ok: hp?.storage?.wearPercent != null ? hp.storage.wearPercent < 80 : null,
                      },
                    ];
                    return (
                      <div className="grid grid-cols-3 gap-4">
                        {punkte.map((p) => (
                          <div key={p.label} className="rounded-lg border border-slate-200 p-3">
                            <span className="text-slate-500 block text-xs mb-1">{p.label}</span>
                            <span className={`text-sm font-medium ${
                              p.ok === true ? 'text-green-600' : p.ok === false ? 'text-red-600' : 'text-slate-600'
                            }`}>
                              {p.wert}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Verlauf der letzten 30 Tage (Tagesverdichtung) */}
                  <DeviceHistoryCharts deviceId={selectedDevice.id} days={30} />

                  {selectedDevice.lastHealthPayload?.system ? (() => {
                    const sys = selectedDevice.lastHealthPayload.system;
                    const formatUptime = (s: number) => {
                      const d = Math.floor(s / 86400);
                      const h = Math.floor((s % 86400) / 3600);
                      const m = Math.floor((s % 3600) / 60);
                      return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
                    };
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">Agent</span><span className="font-mono">{selectedDevice.lastHealthPayload?.agentVersion || '-'}</span></div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">OS</span>{sys.osRelease || '-'}</div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">Kernel</span>{sys.kernel || '-'}</div>
                        <div className="p-3 bg-slate-50 rounded-lg col-span-2"><span className="text-slate-500 block text-xs mb-1">CPU</span>{sys.cpuModel || '-'} ({sys.cpuCores} cores)</div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">Temperatur</span><span className={sys.cpuTempCelsius && sys.cpuTempCelsius > 70 ? 'text-red-600 font-medium' : ''}>{sys.cpuTempCelsius?.toFixed(0) || '-'}°C</span></div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">Load (1/5/15)</span>{sys.cpuLoad1m?.toFixed(2) || '-'} / {sys.cpuLoad5m?.toFixed(2) || '-'} / {sys.cpuLoad15m?.toFixed(2) || '-'}</div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">RAM</span><span className={sys.memPercent && sys.memPercent > 85 ? 'text-red-600 font-medium' : ''}>{sys.memPercent?.toFixed(0) || '-'}%</span> ({sys.memUsedMb ? Math.round(sys.memUsedMb/1024) : '-'}/{sys.memTotalMb ? Math.round(sys.memTotalMb/1024) : '-'} GB)</div>
                        <div className="p-3 bg-slate-50 rounded-lg"><span className="text-slate-500 block text-xs mb-1">Disk</span><span className={sys.diskPercent && sys.diskPercent > 85 ? 'text-red-600 font-medium' : ''}>{sys.diskPercent?.toFixed(0) || '-'}%</span></div>
                        <div className="p-3 bg-slate-50 rounded-lg col-span-2"><span className="text-slate-500 block text-xs mb-1">Uptime</span>{sys.uptimeSeconds ? formatUptime(sys.uptimeSeconds) : '-'}</div>
                      </div>
                    );
                  })() : <div className="text-slate-400 text-sm">Keine System-Daten verfügbar</div>}

                  {/* Fernzugriff (VPN) - gehoert in den System-Tab, nicht ans
                      Ende des Netzwerk-Tabs: Ob das Geraet aus der Ferne
                      erreichbar ist, ist eine Kernaussage ueber das Geraet. */}
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Fernzugriff (NetBird)</h4>
                    {(() => {
                      const vpn = selectedDevice.lastHealthPayload?.vpn;
                      const verbunden = vpn?.netbirdConnected === true;
                      return (
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Status</span>
                            <span className={`flex items-center gap-1.5 font-medium ${verbunden ? 'text-green-600' : 'text-slate-500'}`}>
                              <Globe className="w-3.5 h-3.5" />
                              {verbunden ? 'Verbunden' : vpn ? 'Nicht verbunden' : 'Unbekannt'}
                            </span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">VPN-Adresse</span>
                            <span className="font-mono">{vpn?.netbirdIp ? vpn.netbirdIp.split('/')[0] : '-'}</span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Management</span>
                            <span className="font-mono text-xs break-all">{vpn?.mgmtUrl || '-'}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Connectivity in System Tab */}
                  {selectedDevice.lastHealthPayload?.connectivity && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Konnektivität</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500 block text-xs mb-1">Internet</span>
                          {selectedDevice.lastHealthPayload.connectivity.internetLatencyMs?.toFixed(0) ?? '-'} ms
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500 block text-xs mb-1">Gateway</span>
                          {selectedDevice.lastHealthPayload.connectivity.gatewayLatencyMs?.toFixed(0) ?? '-'} ms
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500 block text-xs mb-1">DNS</span>
                          {selectedDevice.lastHealthPayload.connectivity.dnsLatencyMs?.toFixed(0) ?? '-'} ms
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500 block text-xs mb-1">Packet Loss</span>
                          <span className={selectedDevice.lastHealthPayload.connectivity.packetLossPercent && selectedDevice.lastHealthPayload.connectivity.packetLossPercent > 5 ? 'text-red-600 font-medium' : ''}>
                            {selectedDevice.lastHealthPayload.connectivity.packetLossPercent?.toFixed(1) ?? '-'}%
                          </span>
                        </div>
                      </div>
                      {selectedDevice.lastHealthPayload.connectivity.throttlingDetected && (
                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-orange-700 text-sm">
                          Auffällige Verbindungsqualität (hohe Laufzeit oder Paketverlust)
                        </div>
                      )}
                    </div>
                  )}

                  {/* Datenträger - beim ersten Ingest-Gerät ist die Speicherkarte
                      ausgefallen; diese Werte warnen vor, statt zu überraschen. */}
                  {selectedDevice.lastHealthPayload?.storage && (() => {
                    const sp = selectedDevice.lastHealthPayload.storage;
                    const verschleiss = sp.wearPercent ?? null;
                    const reserve = sp.spareAvailPercent ?? null;
                    const kritisch =
                      (sp.criticalWarning ?? 0) !== 0 ||
                      (verschleiss !== null && verschleiss >= 80) ||
                      (reserve !== null && reserve <= 20) ||
                      (sp.mediaErrors ?? 0) > 0;
                    return (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-slate-700 mb-3">Datenträger</h4>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Verschleiß</span>
                            <span className={verschleiss !== null && verschleiss >= 80 ? 'text-red-600 font-medium' : 'font-medium'}>
                              {verschleiss !== null ? `${verschleiss}%` : '-'}
                            </span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Reserve</span>
                            <span className={reserve !== null && reserve <= 20 ? 'text-red-600 font-medium' : ''}>
                              {reserve !== null ? `${reserve}%` : '-'}
                            </span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Geschrieben</span>
                            {sp.writtenGb != null ? `${sp.writtenGb.toLocaleString('de-DE')} GB` : '-'}
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Betrieb</span>
                            {sp.powerOnHours != null ? `${Math.round(sp.powerOnHours / 24).toLocaleString('de-DE')} Tage` : '-'}
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Stromausfälle</span>
                            <span className={(sp.unsafeShutdowns ?? 0) > 0 ? 'text-amber-600' : ''}>
                              {sp.unsafeShutdowns ?? '-'}
                            </span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Lesefehler</span>
                            <span className={(sp.mediaErrors ?? 0) > 0 ? 'text-red-600 font-medium' : ''}>
                              {sp.mediaErrors ?? '-'}
                            </span>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-500 block text-xs mb-1">Temperatur</span>
                            {sp.temperatureC != null ? `${sp.temperatureC}°C` : '-'}
                          </div>
                        </div>
                        {kritisch && (
                          <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                            <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
                            <span>Datenträger zeigt Verschleiß- oder Fehleranzeichen — Austausch einplanen.</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* NETWORK TAB */}
              {activeTab === 'network' && (
                <div className="space-y-3">
                  {/* MSP Warning: Multiple uplinks without fixed metric */}
                  {(() => {
                    const interfaces = selectedDevice.lastHealthPayload?.network?.interfaces || [];
                    const viewModels = interfaces.map(iface => 
                      buildInterfaceViewModel(
                        iface,
                        selectedDevice.interfaceConfig?.[iface.name],
                        selectedDevice.lastHealthPayload?.network?.defaultGateway,
                        selectedDevice.lastHealthPayload?.network?.dnsServers
                      )
                    );
                    // Nur Karten zaehlen, die wirklich eine Standardroute stellen
                    // koennen: verbunden, nicht isoliert UND mit Gateway. Vorher
                    // zaehlte jede nicht-isolierte Karte mit - auch abgesteckte
                    // und solche ohne Gateway, daher "3 von 3 Uplinks".
                    const wegeInsNetz = viewModels.filter(
                      vm => vm.applied.role !== 'isolated' && vm.linkUp && vm.applied.gateway,
                    );
                    const ohneFestePrioritaet = wegeInsNetz.filter(
                      vm => vm.desired?.metric === null || vm.desired?.metric === undefined,
                    );
                    if (wegeInsNetz.length > 1 && ohneFestePrioritaet.length > 0) {
                      return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-800">
                            {wegeInsNetz.length} Netzwerkkarten könnten den Weg ins Netz übernehmen
                            ({wegeInsNetz.map(v => v.name).join(', ')}), ohne feste Reihenfolge.
                            Der Weg kann dadurch unvorhersehbar wechseln — für genau eine Karte
                            eine feste Priorität setzen oder die anderen auf „Isoliert" stellen.
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Interface Cards mit ViewModel - aktive Verbindung zuerst,
                      nicht verbundene Karten zuletzt (vorher Reihenfolge des
                      Geraets, dadurch stand wlan0 (down) gleichrangig oben) */}
                  {[...(selectedDevice.lastHealthPayload?.network?.interfaces ?? [])]
                    .sort((a, b) => {
                      const rang = (i: InterfaceInfo) =>
                        i.isActiveUplink ? 0 : i.linkUp ? 1 : 2;
                      return rang(a) - rang(b) || a.name.localeCompare(b.name);
                    })
                    .map((iface) => {
                    const vm = buildInterfaceViewModel(
                      iface,
                      selectedDevice.interfaceConfig?.[iface.name],
                      selectedDevice.lastHealthPayload?.network?.defaultGateway,
                      selectedDevice.lastHealthPayload?.network?.dnsServers
                    );
                    const queuedDiffs = vm.diffs.filter(d => d.status === 'queued');
                    
                    return (
                      <div key={iface.name} className={`bg-white border rounded-lg overflow-hidden ${
                        vm.status === 'queued' ? 'border-orange-300' : 
                        vm.isActiveUplink ? 'border-blue-300' : 
                        vm.applied.role === 'isolated' ? 'border-amber-300' : 'border-slate-200'
                      }`}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                          <div className="flex items-center gap-2 flex-wrap">
                            {vm.type === 'wlan' ? (
                              vm.linkUp ? <Wifi className="w-4 h-4 text-green-600" /> : <WifiOff className="w-4 h-4 text-slate-400" />
                            ) : (
                              <Network className={`w-4 h-4 ${vm.linkUp ? 'text-green-600' : 'text-slate-400'}`} />
                            )}
                            <span className="font-medium">{vm.name}</span>
                            <span className="text-xs text-slate-400 uppercase">{vm.type}</span>
                            {/* Funktion in Klartext statt des internen Rollenwerts:
                                "UPLINK" stand vorher auf jeder Karte, weil es nur
                                bedeutet "kein never-default gesetzt" - also der
                                Standard, keine Entscheidung. */}
                            {(() => {
                              // Ohne gemeldete Rolle (Geraet ohne NetworkManager)
                              // zaehlt, was tatsaechlich zutrifft: eine Karte ohne
                              // Gateway ist kein Weg ins Internet.
                              const isoliert =
                                vm.applied.role === 'isolated' ||
                                (!vm.applied.role && vm.linkUp && !vm.applied.gateway);
                              const [text, farbe] = isoliert
                                ? ['Isoliert · nur lokales Netz', 'bg-amber-100 text-amber-700']
                                : !vm.linkUp
                                  ? ['Nicht verbunden', 'bg-slate-100 text-slate-500']
                                  : vm.isActiveUplink
                                    ? ['Aktive Verbindung', 'bg-blue-100 text-blue-700']
                                    : vm.applied.gateway
                                      ? ['Reserve-Verbindung', 'bg-slate-100 text-slate-600']
                                      : ['Lokales Netz · kein Gateway', 'bg-slate-100 text-slate-600'];
                              return (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${farbe}`}>
                                  {text}
                                </span>
                              );
                            })()}
                            {vm.desired?.purpose && (
                              <span className="text-xs text-slate-500">{vm.desired.purpose}</span>
                            )}
                            {vm.status === 'queued' ? (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Queued
                              </span>
                            ) : vm.desired && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                                <Check className="w-3 h-3" /> Applied
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium ${vm.linkUp ? 'text-green-600' : 'text-red-500'}`}>
                              {vm.linkUp ? 'Verbunden' : 'Kein Signal'}
                            </span>
                            <button
                              onClick={() => openInterfaceRoleDialog(iface)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <Settings className="w-3 h-3" /> Konfigurieren
                            </button>
                          </div>
                        </div>
                        
                        {/* Content: Two Columns - Applied | Desired */}
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left: Applied (Device State) */}
                            <div>
                              <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">So läuft es gerade</div>
                              <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">IP</span>
                                  <span className="font-mono">{vm.applied.ipAddress || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Gateway</span>
                                  <span className="font-mono">{vm.applied.gateway || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Methode</span>
                                  <span>{vm.applied.method === 'static' ? 'Statisch' : 'DHCP'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Priorität</span>
                                  <span>{vm.applied.metric ?? 'automatisch'}</span>
                                </div>
                                {vm.wlan && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">WLAN</span>
                                    <span>{vm.wlan.ssid || '-'} ({vm.wlan.signalDbm}dBm)</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Rechts: gespeicherte Vorgabe. Ohne Vorgabe stand hier
                                dreimal "Nicht konfiguriert" - das erklaert nichts. */}
                            <div className={vm.desired ? '' : 'opacity-70'}>
                              <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
                                Vorgabe aus der Verwaltung
                              </div>
                              {vm.desired ? (
                                <div className="space-y-1.5 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Funktion</span>
                                    <span className={queuedDiffs.some(d => d.field === 'role') ? 'text-orange-600 font-medium' : ''}>
                                      {vm.desired.role === 'isolated' ? 'Isoliert (nur lokales Netz)' : 'Weg ins Netz'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Methode</span>
                                    <span className={queuedDiffs.some(d => d.field === 'method') ? 'text-orange-600 font-medium' : ''}>
                                      {vm.desired.method === 'static' ? 'Statisch' : 'DHCP'}
                                    </span>
                                  </div>
                                  {vm.desired.method === 'static' && (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">IP</span>
                                        <span className={`font-mono ${queuedDiffs.some(d => d.field === 'ipAddress') ? 'text-orange-600 font-medium' : ''}`}>
                                          {vm.desired.ipAddress || '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Gateway</span>
                                        <span className={`font-mono ${queuedDiffs.some(d => d.field === 'gateway') ? 'text-orange-600 font-medium' : ''}`}>
                                          {vm.desired.gateway || '-'}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Priorität</span>
                                    <span className={queuedDiffs.some(d => d.field === 'metric') ? 'text-orange-600 font-medium' : ''}>
                                      {vm.desired.metric !== null && vm.desired.metric !== undefined ? vm.desired.metric : 'automatisch'}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-slate-500">
                                  Keine Vorgabe — die Karte übernimmt, was das Netz ihr zuweist
                                  (DHCP). Über „Konfigurieren" lässt sich das festlegen.
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Queued Changes Summary */}
                          {queuedDiffs.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="text-xs text-orange-700 font-medium mb-1">Wartet auf Übernahme durch das Gerät:</div>
                              <div className="flex flex-wrap gap-2">
                                {queuedDiffs.map(d => (
                                  <span key={d.field} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                                    {d.field}: {String(d.appliedValue)} → {String(d.desiredValue)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }) || <div className="text-slate-400 text-sm">Keine Interface-Daten</div>}

                  {/* VPN Interface */}
                  {selectedDevice.lastHealthPayload?.vpn && (
                    <div className={`bg-slate-50 rounded-lg p-4 ${selectedDevice.lastHealthPayload.vpn.netbirdConnected ? 'ring-2 ring-purple-400' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-purple-600" />
                          <span className="font-medium">netbird</span>
                          <span className="text-xs text-slate-500 uppercase">VPN</span>
                        </div>
                        <span className={`text-xs font-medium ${selectedDevice.lastHealthPayload.vpn.netbirdConnected ? 'text-green-600' : 'text-red-500'}`}>
                          {selectedDevice.lastHealthPayload.vpn.netbirdConnected ? 'Verbunden' : 'Nicht verbunden'}
                        </span>
                      </div>
                      {selectedDevice.lastHealthPayload.vpn.netbirdConnected && selectedDevice.lastHealthPayload.vpn.netbirdIp && (
                        <div className="text-sm">
                          <span className="text-slate-500 block text-xs">IP</span>
                          <span className="font-mono font-medium">{selectedDevice.lastHealthPayload.vpn.netbirdIp}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ROLE TAB */}
              {activeTab === 'role' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">Rolle</span>
                      <span className="font-medium">{selectedDevice.role}</span>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">SMB Host</span>
                      <span className="font-mono">{selectedDevice.configJson?.smbHost || '-'}</span>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">State</span>
                      {selectedDevice.lastHealthPayload?.role ? (
                        <span className={`font-medium ${
                          selectedDevice.lastHealthPayload.role.runnerState === 'running' ? 'text-green-600' :
                          selectedDevice.lastHealthPayload.role.runnerState === 'error' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {selectedDevice.lastHealthPayload.role.runnerState}
                        </span>
                      ) : '-'}
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">Active Feeds</span>
                      {selectedDevice.lastHealthPayload?.role?.feedsActive ?? '-'}
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">Config Version</span>
                      v{selectedDevice.configVersion}
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-500 block text-xs mb-1">Status</span>
                      {selectedDevice.configVersion === 0 ? (
                        <span className="text-slate-400">-</span>
                      ) : selectedDevice.lastConfigAppliedVersion === selectedDevice.configVersion ? (
                        <span className="text-green-600 font-medium">Applied</span>
                      ) : (
                        <span className="text-yellow-600 font-medium">Pending</span>
                      )}
                    </div>
                  </div>
                  {selectedDevice.lastHealthPayload?.role?.lastError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {selectedDevice.lastHealthPayload.role.lastError}
                    </div>
                  )}
                  
                  {/* Role Config Editor */}
                  <div className="mt-4 pt-4 border-t">
                    <RoleConfigEditor
                      device={selectedDevice}
                      onSave={async (role, config) => {
                        const success = await handleSaveRoleConfig(selectedDevice.id, role, config);
                        if (success) {
                          const updated = devices.find(d => d.id === selectedDevice.id);
                          if (updated) setSelectedDevice(updated);
                        }
                        return success;
                      }}
                      saving={roleConfigSaving === selectedDevice.id}
                    />
                    {roleConfigError && (
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                        <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
                        <span>{roleConfigError}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* COMMANDS TAB */}
              {activeTab === 'commands' && (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {COMMANDS.map((cmd) => {
                      const CmdIcon = cmd.icon;
                      const isCmdLoading = commandLoading === `${selectedDevice.id}-${cmd.id}`;
                      return (
                        <button
                          key={cmd.id}
                          onClick={() => handleSendCommand(selectedDevice.id, cmd.id)}
                          disabled={!!commandLoading}
                          className="px-4 py-2.5 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          {isCmdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CmdIcon className="w-4 h-4" />}
                          {cmd.label}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Command History */}
                  {commandHistory[selectedDevice.id]?.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Verlauf</h4>
                        <div className="flex items-center gap-2">
                          {commandHistory[selectedDevice.id].some(c => c.status === 'PENDING' || c.status === 'EXECUTING') && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              wartet auf Geraet
                            </span>
                          )}
                          <button
                            onClick={() => loadCommandHistory(selectedDevice.id)}
                            className="flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Aktualisieren
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {commandHistory[selectedDevice.id].slice(0, 15).map((cmd) => {
                          const isExpanded = expandedCommand === cmd.id;
                          const hasOutput = cmd.output && cmd.output.length > 0;
                          const durationSec = cmd.executedAt && cmd.createdAt 
                            ? ((new Date(cmd.executedAt).getTime() - new Date(cmd.createdAt).getTime()) / 1000).toFixed(1)
                            : null;
                          return (
                            <div key={cmd.id} className="bg-slate-50 rounded-lg overflow-hidden">
                              <div 
                                className={`flex items-center justify-between text-sm py-2.5 px-3 ${hasOutput ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                                onClick={() => hasOutput && setExpandedCommand(isExpanded ? null : cmd.id)}
                              >
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className="font-mono text-slate-700 font-medium">{cmd.command}</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${COMMAND_STATUS_COLORS[cmd.status] || 'bg-slate-100'}`}>
                                    {cmd.status.toLowerCase()}
                                  </span>
                                  {durationSec && (
                                    <span className="text-xs text-slate-400">{durationSec}s</span>
                                  )}
                                  {hasOutput && (
                                    <span className="text-xs text-blue-500 flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      Output
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400 text-xs">{formatTimeAgo(cmd.createdAt)}</span>
                                  {hasOutput && (
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  )}
                                </div>
                              </div>
                              {/* Expandable Output - uses OutputPanel */}
                              {isExpanded && hasOutput && (
                                <div className="border-t border-slate-200 p-3" onClick={(e) => e.stopPropagation()}>
                                  <OutputPanel content={cmd.output ?? ''} label={`${cmd.command} Output`} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* LOGS TAB - Agent Events mit Kategorien-Filter */}
              {activeTab === 'logs' && (() => {
                // Lade Events beim Tab-Wechsel falls nicht vorhanden
                if (!agentEvents && !agentEventsLoading && !agentEventsError) {
                  loadAgentEvents(selectedDevice.id, Array.from(selectedCategories), Array.from(selectedLevels));
                }
                
                const rawEvents = agentEvents?.events || [];
                
                // Dedup: Gleiche Events (event+msg) innerhalb 60s gruppieren
                type EventType = typeof rawEvents[number];
                const groupEvents = (events: EventType[]) => {
                  const groups: Array<{
                    key: string;
                    event: string;
                    level: string;
                    category: string;
                    msg: string;
                    count: number;
                    firstTs: string;
                    lastTs: string;
                    details?: Record<string, unknown>;
                    instances: EventType[];
                  }> = [];
                  
                  events.forEach(evt => {
                    const key = `${evt.event}:${evt.msg}`;
                    const lastGroup = groups[groups.length - 1];
                    
                    if (lastGroup && lastGroup.key === key) {
                      const timeDiff = new Date(evt.ts).getTime() - new Date(lastGroup.lastTs).getTime();
                      if (Math.abs(timeDiff) < 60000) {
                        lastGroup.count++;
                        lastGroup.lastTs = evt.ts;
                        lastGroup.instances.push(evt);
                        return;
                      }
                    }
                    
                    groups.push({
                      key,
                      event: evt.event,
                      level: evt.level,
                      category: evt.category,
                      msg: evt.msg,
                      count: 1,
                      firstTs: evt.ts,
                      lastTs: evt.ts,
                      details: evt.details,
                      instances: [evt],
                    });
                  });
                  
                  return groups;
                };
                
                // Filter lokal nach Event-Typ (logFilter State)
                const filteredEvents = logFilter 
                  ? rawEvents.filter(e => e.event === logFilter)
                  : rawEvents;
                
                const groupedEvents = groupEvents(filteredEvents);
                const eventTypes = [...new Set(rawEvents.map(e => e.event))].sort();
                
                const toggleEvent = (key: string) => {
                  setExpandedEvents(prev => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                };

                const formatTimeAgoShort = (isoString: string | null) => {
                  if (!isoString) return null;
                  const diffMs = Date.now() - new Date(isoString).getTime();
                  const diffSec = Math.floor(diffMs / 1000);
                  if (diffSec < 60) return `vor ${diffSec}s`;
                  const diffMin = Math.floor(diffSec / 60);
                  if (diffMin < 60) return `vor ${diffMin}min`;
                  const diffH = Math.floor(diffMin / 60);
                  return `vor ${diffH}h`;
                };

                const toggleCategory = (cat: string) => {
                  const newSet = new Set(selectedCategories);
                  if (newSet.has(cat)) newSet.delete(cat);
                  else newSet.add(cat);
                  setSelectedCategories(newSet);
                };

                const toggleLevel = (lvl: string) => {
                  const newSet = new Set(selectedLevels);
                  if (newSet.has(lvl)) newSet.delete(lvl);
                  else newSet.add(lvl);
                  setSelectedLevels(newSet);
                };

                const applyFilters = () => {
                  setAgentEvents(null);
                  loadAgentEvents(selectedDevice.id, Array.from(selectedCategories), Array.from(selectedLevels));
                };
                
                return (
                  <div className="space-y-3">
                    {/* Filter Bar */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Kategorien */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-slate-500 font-medium">Kategorien:</span>
                          {AVAILABLE_CATEGORIES.map(cat => (
                            <button
                              key={cat}
                              onClick={() => toggleCategory(cat)}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                selectedCategories.has(cat)
                                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}
                            >
                              {CATEGORY_LABELS[cat] || cat}
                            </button>
                          ))}
                        </div>
                        <div className="h-4 w-px bg-slate-300" />
                        {/* Levels */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500 font-medium">Level:</span>
                          {['info', 'warn', 'error'].map(lvl => (
                            <button
                              key={lvl}
                              onClick={() => toggleLevel(lvl)}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                selectedLevels.has(lvl)
                                  ? lvl === 'error' ? 'bg-red-100 border-red-300 text-red-700'
                                    : lvl === 'warn' ? 'bg-amber-100 border-amber-300 text-amber-700'
                                    : 'bg-slate-100 border-slate-300 text-slate-700'
                                  : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                              }`}
                            >
                              {lvl}
                            </button>
                          ))}
                        </div>
                        <div className="h-4 w-px bg-slate-300" />
                        {/* Apply Button */}
                        <button
                          onClick={applyFilters}
                          disabled={agentEventsLoading}
                          className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Filter anwenden
                        </button>
                      </div>
                    </div>

                    {/* Events List */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-700 font-medium text-sm">Agent Events</span>
                          <span className="text-slate-400 text-xs">
                            ({rawEvents.length} Events{agentEvents?.truncated ? '+' : ''}, {groupedEvents.length} Gruppen)
                          </span>
                          {agentEvents?.truncated && (
                            <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Limit erreicht</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={logFilter || ''}
                            onChange={(e) => setLogFilter(e.target.value || null)}
                            className="bg-white text-slate-700 text-xs rounded px-2 py-1 border border-slate-200"
                          >
                            <option value="">Alle Event-Typen</option>
                            {eventTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          <button
                            onClick={applyFilters}
                            disabled={agentEventsLoading}
                            className="text-slate-500 hover:text-slate-700 p-1 rounded hover:bg-slate-100 disabled:opacity-50"
                            title="Aktualisieren"
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${agentEventsLoading ? 'animate-spin' : ''}`} />
                          </button>
                          {agentEvents?.newestLogAt && (
                            <span className="text-slate-400 text-xs">
                              Stand: {formatTimeAgoShort(agentEvents.newestLogAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100 overflow-y-auto" style={{ maxHeight: '400px' }}>
                        {agentEventsLoading ? (
                          <div className="flex items-center justify-center p-6 gap-2 text-slate-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Lade Events...</span>
                          </div>
                        ) : agentEventsError ? (
                          <div className="p-6 text-center">
                            <div className="text-red-600 text-sm mb-2">{agentEventsError}</div>
                            <button
                              onClick={applyFilters}
                              className="text-xs px-3 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                            >
                              Erneut versuchen
                            </button>
                          </div>
                        ) : groupedEvents.length > 0 ? (
                          groupedEvents.map((group, idx) => {
                            const isExpanded = expandedEvents.has(group.key + idx);
                            const hasDetails = group.details && Object.keys(group.details).length > 0;
                            const isGrouped = group.count > 1;
                            return (
                              <div 
                                key={group.key + idx} 
                                className={`px-4 py-2.5 ${(hasDetails || isGrouped) ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                                onClick={() => (hasDetails || isGrouped) && toggleEvent(group.key + idx)}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="text-slate-400 text-xs w-12 flex-shrink-0 font-mono">
                                    {new Date(group.firstTs).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className="flex-shrink-0">
                                    {group.level === 'error' ? (
                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                    ) : group.level === 'warn' ? (
                                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    ) : (
                                      <Info className="w-4 h-4 text-slate-400" />
                                    )}
                                  </span>
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">
                                    {CATEGORY_LABELS[group.category] || group.category}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                    group.level === 'error' ? 'bg-red-100 text-red-700' :
                                    group.level === 'warn' ? 'bg-amber-100 text-amber-700' :
                                    'bg-blue-50 text-blue-600'
                                  }`}>
                                    {group.event}
                                  </span>
                                  {isGrouped && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                      x{group.count}
                                    </span>
                                  )}
                                  <span className={`flex-1 text-sm truncate ${
                                    group.level === 'error' ? 'text-red-700' :
                                    group.level === 'warn' ? 'text-amber-700' :
                                    'text-slate-600'
                                  }`}>
                                    {group.msg}
                                  </span>
                                  {(hasDetails || isGrouped) && (
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  )}
                                </div>
                                {isExpanded && (
                                  <div className="mt-2 ml-16 text-xs">
                                    {isGrouped && (
                                      <div className="text-slate-500 mb-2">
                                        {group.count} Events von {new Date(group.firstTs).toLocaleTimeString('de-DE')} bis {new Date(group.lastTs).toLocaleTimeString('de-DE')}
                                      </div>
                                    )}
                                    {hasDetails && (
                                      <NetworkEventDetails details={group.details!} event={group.event} />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-slate-400 italic p-6 text-center text-sm">
                            {logFilter ? `Keine Events vom Typ "${logFilter}".` : 'Keine Events in den letzten 24h.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>
        </div>
      )}

      {/* Interface Role Dialog */}
      {editingInterface && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-lg">Interface-Rolle ändern: {editingInterface.name}</h3>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Current Role Display */}
              <div className="text-sm">
                <span className="text-slate-500">Aktuelle Rolle: </span>
                <span className={`font-medium ${editingInterface.role === 'isolated' ? 'text-amber-700' : 'text-blue-700'}`}>
                  {editingInterface.role === 'isolated' ? 'ISOLATED' : 'UPLINK'}
                </span>
                {editingInterface.isActiveUplink && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Aktiver Internet-Uplink</span>
                )}
              </div>

              {/* Current Pi State */}
              <div className="p-3 bg-slate-100 rounded-lg text-sm">
                <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                  <Cpu className="w-3 h-3" /> Aktueller Zustand (Gerät)
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Methode:</span> <span className="font-mono">{editingInterface.dhcpOrStatic || '-'}</span></div>
                  <div><span className="text-slate-500">Rolle:</span> <span className="font-mono">{editingInterface.role || 'uplink'}</span></div>
                  {editingInterface.ipv4Addrs?.[0] && (
                    <div className="col-span-2"><span className="text-slate-500">IP:</span> <span className="font-mono">{editingInterface.ipv4Addrs[0]}</span></div>
                  )}
                  {editingInterface.gateway && (
                    <div><span className="text-slate-500">Gateway:</span> <span className="font-mono">{editingInterface.gateway}</span></div>
                  )}
                </div>
              </div>

              {/* Pending WebApp Config (if different) */}
              {(() => {
                const savedConfig = selectedDevice?.interfaceConfig?.[editingInterface.name];
                if (!savedConfig) return null;
                return (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                    <div className="text-xs font-medium text-orange-700 mb-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Gespeicherte Config (WebApp)
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {savedConfig.method && (
                        <div><span className="text-orange-600">Methode:</span> <span className="font-mono">{savedConfig.method}</span></div>
                      )}
                      {savedConfig.role && (
                        <div><span className="text-orange-600">Rolle:</span> <span className="font-mono">{savedConfig.role}</span></div>
                      )}
                      {savedConfig.ipAddress && (
                        <div className="col-span-2"><span className="text-orange-600">IP:</span> <span className="font-mono">{savedConfig.ipAddress}</span></div>
                      )}
                      {savedConfig.gateway && (
                        <div><span className="text-orange-600">Gateway:</span> <span className="font-mono">{savedConfig.gateway}</span></div>
                      )}
                      {savedConfig.dns && savedConfig.dns.length > 0 && (
                        <div className="col-span-2"><span className="text-orange-600">DNS:</span> <span className="font-mono">{savedConfig.dns.join(', ')}</span></div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Role Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Neue Rolle:</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="role"
                      value="uplink"
                      checked={interfaceRoleForm.role === 'uplink'}
                      onChange={() => setInterfaceRoleForm(f => ({ ...f, role: 'uplink' }))}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium">Uplink</div>
                      <div className="text-xs text-slate-500">Kann für Internet genutzt werden</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="role"
                      value="isolated"
                      checked={interfaceRoleForm.role === 'isolated'}
                      onChange={() => setInterfaceRoleForm(f => ({ ...f, role: 'isolated' }))}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium">Isolated</div>
                      <div className="text-xs text-slate-500">Nur lokales Netz, z.B. Anlagensteuerung (XP)</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Purpose Field (only for isolated) */}
              {interfaceRoleForm.role === 'isolated' && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-700">Zweck (optional):</label>
                  <input
                    type="text"
                    value={interfaceRoleForm.purpose}
                    onChange={(e) => setInterfaceRoleForm(f => ({ ...f, purpose: e.target.value }))}
                    placeholder="z.B. XP-Netzwerk TWA"
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              )}

              {/* IP Configuration Method */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">IP-Konfiguration:</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setInterfaceRoleForm(f => ({ ...f, method: 'dhcp' }))}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      interfaceRoleForm.method === 'dhcp' 
                        ? 'bg-blue-50 border-blue-500 text-blue-700' 
                        : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    DHCP
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterfaceRoleForm(f => ({ ...f, method: 'static' }))}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                      interfaceRoleForm.method === 'static' 
                        ? 'bg-blue-50 border-blue-500 text-blue-700' 
                        : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    Statisch
                  </button>
                </div>
              </div>

              {/* Static IP Configuration Fields */}
              {interfaceRoleForm.method === 'static' && (() => {
                // CIDR to Subnet Mask conversion
                const cidrToSubnet = (cidr: string): string | null => {
                  const match = cidr.match(/\/(\d+)$/);
                  if (!match) return null;
                  const prefix = parseInt(match[1], 10);
                  if (prefix < 0 || prefix > 32) return null;
                  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
                  return [(mask >>> 24) & 255, (mask >>> 16) & 255, (mask >>> 8) & 255, mask & 255].join('.');
                };
                const subnetMask = cidrToSubnet(interfaceRoleForm.ipAddress);
                return (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700">IP-Adresse (CIDR):</label>
                    <input
                      type="text"
                      value={interfaceRoleForm.ipAddress}
                      onChange={(e) => {
                        setInterfaceRoleForm(f => ({ ...f, ipAddress: e.target.value }));
                        setIpValidationError(null);
                      }}
                      placeholder="192.168.100.10/24"
                      className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${ipValidationError ? 'border-red-500' : ''}`}
                    />
                    {ipValidationError ? (
                      <div className="text-xs text-red-600 mt-1">{ipValidationError}</div>
                    ) : subnetMask && (
                      <div className="text-xs text-slate-500 mt-1">
                        Subnet Mask: <span className="font-mono">{subnetMask}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700">Gateway:</label>
                    <input
                      type="text"
                      value={interfaceRoleForm.gateway}
                      onChange={(e) => setInterfaceRoleForm(f => ({ ...f, gateway: e.target.value }))}
                      placeholder="192.168.100.1"
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700">DNS (kommagetrennt):</label>
                    <input
                      type="text"
                      value={interfaceRoleForm.dns}
                      onChange={(e) => setInterfaceRoleForm(f => ({ ...f, dns: e.target.value }))}
                      placeholder="8.8.8.8, 8.8.4.4"
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    />
                  </div>
                </div>
                );
              })()}

              {/* Route Metric (only for uplink) */}
              {interfaceRoleForm.role === 'uplink' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Route-Prioritaet (Metric):</label>
                  <select
                    value={interfaceRoleForm.metricPreset}
                    onChange={(e) => setInterfaceRoleForm(f => ({ ...f, metricPreset: e.target.value as typeof f.metricPreset }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="auto">Auto (DHCP/System entscheidet)</option>
                    <option value="preferred">Bevorzugt (10)</option>
                    <option value="standard">Standard (100)</option>
                    <option value="fallback">Fallback (200)</option>
                    <option value="custom">Benutzerdefiniert...</option>
                  </select>
                  {interfaceRoleForm.metricPreset === 'custom' && (
                    <input
                      type="number"
                      min="1"
                      max="9999"
                      value={interfaceRoleForm.metricCustom}
                      onChange={(e) => setInterfaceRoleForm(f => ({ ...f, metricCustom: e.target.value }))}
                      placeholder="1-9999"
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    />
                  )}
                  <p className="text-xs text-slate-500">
                    Kleinere Zahl = bevorzugte Standardroute. Beispiel: 10 bevorzugt, 100 normal, 200 Fallback.
                  </p>
                </div>
              )}

              {/* Warning for active uplink */}
              {showUplinkWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2 text-amber-800">
                    <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">WARNUNG</div>
                      <p className="text-sm mt-1">
                        Du bearbeitest das <strong>AKTIVE Internet-Interface</strong> ({editingInterface.name}).
                      </p>
                      <p className="text-sm mt-2">
                        Wenn du dieses Interface auf "Isolated" setzt, verliert das Gerät möglicherweise die Verbindung zur WebApp!
                      </p>
                      <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={uplinkWarningConfirmed}
                          onChange={(e) => setUplinkWarningConfirmed(e.target.checked)}
                        />
                        Ich habe physischen Zugang zum Gerät
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={closeInterfaceRoleDialog}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Abbrechen
              </button>
              <button
                onClick={handleInterfaceRoleSave}
                disabled={interfaceConfigSaving || (showUplinkWarning && !uplinkWarningConfirmed)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {interfaceConfigSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {showUplinkWarning ? 'Trotzdem ändern' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban Device Confirmation Dialog */}
      {showBanDialog && selectedDevice && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBanDialog(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Gerät entfernen</h3>
                <p className="text-sm text-slate-500">{selectedDevice.deviceId}</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800">
                <strong>Achtung:</strong> Das Gerät wird sich beim nächsten Health-Check selbst deinstallieren 
                und in den Zustand vor der Installation zurücksetzen.
              </p>
            </div>

            <div className="space-y-2 mb-4">
              <label className="text-sm font-medium text-slate-700">Grund (optional):</label>
              <input
                type="text"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="z.B. Gerät defekt, nicht mehr benötigt..."
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowBanDialog(false); setBanReason(''); }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Abbrechen
              </button>
              <button
                onClick={handleBanDevice}
                disabled={banLoading}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {banLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Gerät entfernen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Code Dialog */}
      {showJoinCodeDialog && joinCode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowJoinCodeDialog(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Neues Gerät hinzufügen</h3>
              <p className="text-sm text-slate-500 mt-1">
                Verwende diesen Code beim Installieren des Agents
              </p>
            </div>

            <div className="mb-4">
              {/* Enrollment URL */}
              <div className="bg-slate-100 rounded-lg p-4">
                <div className="text-xs text-slate-500 mb-2">Enrollment-URL</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 font-mono text-sm text-slate-900 break-all">
                    {window.location.origin}/enroll/{joinCode.code}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/enroll/${joinCode.code}`);
                      setCopiedField('code');
                      setTimeout(() => setCopiedField(null), 2000);
                    }}
                    className="p-2 hover:bg-slate-200 rounded-lg transition-colors shrink-0"
                    title="URL kopieren"
                  >
                    {copiedField === 'code' ? (
                      <CheckIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 text-center mb-4">
              Gültig bis {new Date(joinCode.expiresAt).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' })} Uhr
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowJoinCodeDialog(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300"
              >
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

